import React from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  Button,
  Switch,
  FormControlLabel,
  Chip,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  Lock as PrivacyIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { DataPrivacySettings as DataPrivacySettingsType, PermissionUtils } from './types';
import { themeBackgrounds } from '../../theme/theme';
import { useSelector } from 'react-redux';

interface DataPrivacySettingsProps {
  dataPrivacySettings: DataPrivacySettingsType;
  setDataPrivacySettings: React.Dispatch<React.SetStateAction<DataPrivacySettingsType>>;
  onSave: () => Promise<void>;
  permissionUtils: PermissionUtils;
}

const DataPrivacySettings: React.FC<DataPrivacySettingsProps> = ({
  dataPrivacySettings,
  setDataPrivacySettings,
  onSave,
  permissionUtils
}) => {
  const { canEditSection } = permissionUtils;
  const activePlanDataFeatures = useSelector((state: any) => state.auth.userDetails?.activePlanDetails?.features?.analytics || null);
  console.log('Active Plan Data Features:', activePlanDataFeatures);

  const handleDataPrivacyChange = (field: keyof DataPrivacySettingsType) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setDataPrivacySettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PrivacyIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Data & Privacy Settings</Typography>
          {!canEditSection('privacy') && (
            <Chip
              label="Read Only"
              color="default"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </Box>

        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '48px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dataPrivacySettings.allowDataAccess}
                    onChange={handleDataPrivacyChange('allowDataAccess')}
                    // disabled={!canEditSection('privacy')}
                    disabled={(activePlanDataFeatures ? !activePlanDataFeatures.export_data : true) || !canEditSection('privacy')}
                  />
                }
                label="Allow Export Data Access"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '48px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dataPrivacySettings.accessUserAnalytics}
                    onChange={handleDataPrivacyChange('accessUserAnalytics')}
                    // disabled={!canEditSection('privacy')}
                    disabled={(activePlanDataFeatures ? !activePlanDataFeatures.user_analytics : true) || !canEditSection('privacy')}
                  />
                }
                label="Access User Analytics"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '48px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dataPrivacySettings.accessChatAnalytics}
                    onChange={handleDataPrivacyChange('accessChatAnalytics')}
                    // disabled={!canEditSection('privacy')}
                    disabled={(activePlanDataFeatures ? !activePlanDataFeatures.chat_analytics : true) || !canEditSection('privacy')}
                  />
                }
                label="Access Chat Analytics"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '48px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dataPrivacySettings.accessCallAnalytics}
                    onChange={handleDataPrivacyChange('accessCallAnalytics')}
                    // disabled={!canEditSection('privacy')}
                    disabled={(activePlanDataFeatures ? !activePlanDataFeatures.call_analytics : true) || !canEditSection('privacy')}
                  />
                }
                label="Access Call Analytics"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: '48px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={dataPrivacySettings.accessSentimentAnalysis}
                    onChange={handleDataPrivacyChange('accessSentimentAnalysis')}
                    // disabled={!canEditSection('privacy')}
                    disabled={(activePlanDataFeatures ? !activePlanDataFeatures.sentiment_analytics : true) || !canEditSection('privacy')}
                  />
                }
                label="Access Sentiment Analysis"
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={onSave}
                disabled={!canEditSection('privacy')}
                sx={{
                  mr: 1,
                  background: themeBackgrounds.default,
                  color: '#ffffff',
                  '&:hover': {
                    background: themeBackgrounds.default,
                    opacity: 0.9,
                  },
                }}
              >
                Save Data & Privacy Settings
              </Button>
            </Box>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
};

export default DataPrivacySettings;